import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Modal } from "react-bootstrap";
import { showSuccess, showError } from "toast/toast";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import TablePaginationControls from "components/TableandmobilePaginationcontrol";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import { EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import { getAuthCookie } from "../../utils/authCookies";

type City = {
  _id: string;
  stateId: string;
  name: string;
  code: string;
  created_by?: {
    id: string;
    name: string;
  };
  updated_by?: {
    id: string;
    name: string;
  };
  created_at?: string;
  updated_at?: string;
};

type State = {
  _id: string;
  countryId: string;
  name: string;
  code: string;
};

const columnHelper = createColumnHelper<City>();

const SuperAdminCities = () => {
  const { stateId } = useParams<{ stateId: string }>();
  const [cities, setCities] = useState<City[]>([]);
  const [data, setData] = useState<City[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const token = getAuthCookie("sessionToken");

  const location = useLocation();
  const navigate = useNavigate();

  // Transform API data to City format
  useEffect(() => {
    if (cities && cities.length > 0) {
      setData(cities);
    } else {
      setData([]);
    }
  }, [cities]);

  // Fetch states for the dropdown
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/location/states`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        setStates(data || []);
      } catch (err) {
        console.error("Error fetching states:", err);
      }
    };
    fetchStates();
  }, [token]);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/location/cities/state/${stateId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const result = await response.json();
        console.log("Fetched Cities:", result);
        setCities(result || []);
        setData(result || []);
      } catch (err) {
        setCities([]);
        setData([]);
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCities();
  }, [token, stateId]);

  // Formik for Add/Edit Modal
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      stateId: selectedCity?.stateId || stateId || '',
      cityName: selectedCity?.name || '',
      cityCode: selectedCity?.code || '',
    },
    validationSchema: Yup.object({
      stateId: Yup.string().required('State is required'),
      cityName: Yup.string()
        .min(2, 'City name must be at least 2 characters long')
        .required('City name is required'),
      cityCode: Yup.string()
        .min(2, 'City code must be at least 2 characters long')
        .required('City code is required'),
    }),
    onSubmit: async (values, { resetForm }) => {
      setSubmitting(true);
      try {
        const url = modalMode === 'add'
          ? `${process.env.REACT_APP_API_URL}/location/cities`
          : `${process.env.REACT_APP_API_URL}/location/cities/${selectedCity?._id}`;

        const method = modalMode === 'add' ? 'POST' : 'PUT';

        const response = await fetch(url, {
          method,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...(modalMode === 'edit' && { id: selectedCity?._id }),
            stateId: values.stateId,
            stateName: '',
            name: values.cityName,
            code: values.cityCode,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to submit form');
        }

        showSuccess(modalMode === 'add' ? 'City added successfully!' : 'City updated successfully!');

        setShowModal(false);
        resetForm();
        setSelectedCity(null);

        // Refresh the data
        window.location.reload();

      } catch (error) {
        showError('An error occurred while saving the city.');
        console.error('Error submitting form:', error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleAddClick = () => {
    setModalMode('add');
    setSelectedCity(null);
    formik.resetForm();
    setShowModal(true);
  };

  const handleEditClick = (city: City) => {
    setModalMode('edit');
    setSelectedCity(city);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCity(null);
    formik.resetForm();
  };

  const handleDelete = async (cityId: string) => {
    setDeleting(cityId);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/location/cities/${cityId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete the city.");
      }

      showSuccess("City deleted successfully!");
      setCities((prev) =>
        prev.filter((city) => city._id !== cityId)
      );
      setDeleting(null);
      navigate(0);
    } catch (err) {
      showError("Failed to delete the city.");
      setError(
        err instanceof Error
          ? err.message
          : "An unknown error occurred while deleting."
      );
      setDeleting(null);
    }
  };

  // Define table columns using TanStack Table
  const columns = useMemo(() => {
    return [
      columnHelper.accessor("_id", {
        id: "id",
        header: () => <div className="th-text">City ID</div>,
        cell: (info) => (
          <div className="fw-bold fs-16" style={{ color: '#2563eb' }}>
            {info.row.index + 1}
          </div>
        ),
      }),

      columnHelper.accessor("name", {
        id: "name",
        header: () => <div className="th-text">City Name</div>,
        cell: (info) => (
          <div className="fw-normal fs-16" style={{ color: '#374151' }}>
            {info.getValue()}
          </div>
        ),
      }),

      columnHelper.accessor("code", {
        id: "code",
        header: () => <div className="th-text">Code</div>,
        cell: (info) => (
          <div className="fw-normal fs-16" style={{ color: '#374151' }}>
            {info.getValue()}
          </div>
        ),
      }),

      columnHelper.display({
        id: "actions",
        header: () => <div className="th-text">Actions</div>,
        cell: (info) => (
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-warning icon-square p-0"
              style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              title="Edit"
              onClick={() => handleEditClick(info.row.original)}
            >
              <i className="ri-pencil-fill fs-5" />
            </button>
            <DeleteIconButton
              title="Delete"
              className="icon-square danger"
              onClick={() =>
                window.confirm("Are you sure you want to delete this city?") &&
                handleDelete(info.row.original._id)
              }
            />
          </div>
        ),
      }),
    ];
  }, [handleDelete]);

  const table = useReactTable({
    data,
    columns,
    pageCount: Math.ceil(data.length / pagination.pageSize),
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: false,
  });

  return (
    <div className="content">
      <style>
        {`
          .btn-warning.icon-square:hover i {
            color: #856404 !important;
          }
        `}
      </style>
      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        {/* Header */}
        <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
          <h5 className="mb-0">Cities</h5>
          <div className="d-flex gap-2">
            <button
              type="button"
              onClick={handleAddClick}
              className="btn btn-primary table-addbutton"
            >
              <i className="ri-add-line me-1" />
              Add City
            </button>
          </div>
        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />
        
        {/* DESKTOP (≥ 1022px) */}
        <div className="rdt-desktop shadow-sm">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              <DesktopTableParts table={table} />
            </table>
          </div>
        </div>

        {/* MOBILE (≤ 1021px) */}
        <div className="rdt-mobile">
          <MobileCardsOnly
            table={table}
            idColId="id"
            titleColId="name"
            actionColId="actions"
          />
        </div>
      </div>
      
      {table.getRowModel().rows.length === 0 ? (
        <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
          {loading ? "Loading cities..." : error ? `Error: ${error}` : "No cities found"}
        </div>
      ) : (
        <div className="mt-4 mb-4">
          <TablePaginationControls table={table} />
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title className="fw-bold">
            {modalMode === 'add' ? 'Add City' : 'Edit City'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <form onSubmit={formik.handleSubmit}>
            <div className="row g-3">
              <div className="col-md-12">
                <label className="form-label fw-semibold">
                  State <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-control ${formik.touched.stateId && formik.errors.stateId ? 'is-invalid' : ''}`}
                  id="stateId"
                  name="stateId"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.stateId}
                >
                  <option value="">- Select State -</option>
                  {states && states.map((state) => (
                    <option key={state._id} value={state._id}>
                      {state.name}
                    </option>
                  ))}
                </select>
                {formik.touched.stateId && formik.errors.stateId ? (
                  <div className="invalid-feedback d-block">{formik.errors.stateId}</div>
                ) : null}
              </div>
              <div className="col-md-12">
                <label className="form-label fw-semibold">
                  City Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${formik.touched.cityName && formik.errors.cityName ? 'is-invalid' : ''}`}
                  id="cityName"
                  name="cityName"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.cityName}
                  placeholder="Enter city name"
                />
                {formik.touched.cityName && formik.errors.cityName ? (
                  <div className="invalid-feedback">{formik.errors.cityName}</div>
                ) : null}
              </div>
              <div className="col-md-12">
                <label className="form-label fw-semibold">
                  City Code <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${formik.touched.cityCode && formik.errors.cityCode ? 'is-invalid' : ''}`}
                  id="cityCode"
                  name="cityCode"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.cityCode}
                  placeholder="Enter city code"
                />
                {formik.touched.cityCode && formik.errors.cityCode ? (
                  <div className="invalid-feedback">{formik.errors.cityCode}</div>
                ) : null}
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer className="border-top pt-3">
          <button
            type="button"
            className="btn back-btn surface-btn fw-bold fs-16"
            onClick={handleCloseModal}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary save-btn surface-btn fw-bold fs-16"
            onClick={() => formik.handleSubmit()}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              modalMode === 'add' ? 'Add City' : 'Update City'
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SuperAdminCities;
